/* Public — /target-zips
   Publishers pick an offer from the dropdown and see the current list
   of target zip codes, ready to copy. DMM dark/blue theme. */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Loader2,
  Copy,
  CheckCircle,
  Search,
  RefreshCcw,
  ChevronDown,
  Facebook,
  Search as GoogleIcon,
  ShieldAlert,
  Globe2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  {
    key: "facebook",
    label: "Facebook Marketplace",
    short: "Facebook",
    icon: Facebook,
    swatch: "from-blue-600 to-sky-500",
    accent: "text-sky-300",
    accentSoft: "text-sky-200",
    bg: "bg-sky-500/10",
    border: "border-sky-500/40",
    ring: "ring-sky-500/40",
    dot: "bg-sky-400",
    tile: "hover:border-sky-400/60",
    gradFrom: "from-sky-400",
  },
  {
    key: "google",
    label: "Google Ads",
    short: "Google",
    icon: GoogleIcon,
    swatch: "from-amber-500 to-orange-500",
    accent: "text-amber-300",
    accentSoft: "text-amber-200",
    bg: "bg-amber-500/10",
    border: "border-amber-500/40",
    ring: "ring-amber-500/40",
    dot: "bg-amber-400",
    tile: "hover:border-amber-400/60",
    gradFrom: "from-amber-300",
  },
];
const catByKey = (k) => CATEGORIES.find((c) => c.key === k) || CATEGORIES[0];

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

export default function TargetZipsPage() {
  const [category, setCategory] = useState("facebook");
  const [offers, setOffers] = useState([]);
  const [categoryStats, setCategoryStats] = useState({});
  const [syncedAt, setSyncedAt] = useState(null);
  const [selected, setSelected] = useState("");
  const [zips, setZips] = useState([]);
  const [zipsSyncedAt, setZipsSyncedAt] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingZips, setLoadingZips] = useState(false);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [copied, setCopied] = useState("");

  const cat = catByKey(category);

  // Load offer list (scoped by category)
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingList(true);
      setError("");
      setSelected("");
      setZips([]);
      try {
        const res = await fetch(`${API}/api/target-zips/offers?category=${category}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!alive) return;
        setOffers(data.offers || []);
        setSyncedAt(data.synced_at || null);
        setCategoryStats(data.category_stats || {});
        if ((data.offers || []).length > 0) setSelected(data.offers[0].offer_name);
      } catch (e) {
        if (alive) setError("Unable to load offers right now. Please refresh in a moment.");
      } finally {
        if (alive) setLoadingList(false);
      }
    })();
    return () => { alive = false; };
  }, [category]);

  // Load zips for selection (scoped by category)
  useEffect(() => {
    if (!selected) { setZips([]); return; }
    let alive = true;
    (async () => {
      setLoadingZips(true);
      try {
        const res = await fetch(`${API}/api/target-zips/${encodeURIComponent(selected)}?category=${category}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!alive) return;
        setZips(data.zip_codes || []);
        setZipsSyncedAt(data.synced_at || null);
      } catch {
        if (alive) setZips([]);
      } finally {
        if (alive) setLoadingZips(false);
      }
    })();
    return () => { alive = false; };
  }, [selected, category]);

  const filteredZips = useMemo(() => {
    if (!q.trim()) return zips;
    const needle = q.trim();
    return zips.filter((z) => z.startsWith(needle) || z.includes(needle));
  }, [zips, q]);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(filteredZips.join("\n"));
      setCopied("all");
      setTimeout(() => setCopied(""), 1600);
    } catch { /* clipboard unavailable */ }
  };

  const copyCommaSeparated = async () => {
    try {
      await navigator.clipboard.writeText(filteredZips.join(", "));
      setCopied("csv");
      setTimeout(() => setCopied(""), 1600);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 relative overflow-x-hidden">
      {/* Ambient background gradient */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-blue-600/15 blur-3xl" />
        <div className="absolute top-40 -right-32 w-[540px] h-[540px] rounded-full bg-indigo-600/15 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 w-[420px] h-[420px] rounded-full bg-purple-600/10 blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative border-b border-slate-800/70 bg-slate-950/70 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="target-zips-logo">
            <img
              src="/logo_no_bg.png"
              alt="Digital Master Media LLC"
              className="h-12 sm:h-14 w-auto object-contain"
            />
          </Link>
          <Link
            to="/offers"
            className="text-[12.5px] font-semibold text-slate-300 hover:text-white transition"
            data-testid="target-zips-back-offers"
          >
            View PPC Offers →
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-6">
        <div className={`inline-flex items-center gap-2 rounded-full border ${cat.border} ${cat.bg} px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${cat.accent} mb-4`}>
          <MapPin className="w-3.5 h-3.5" /> Target Zip Codes · {cat.short}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white">
          Live coverage areas{" "}
          <span className={`bg-gradient-to-r ${cat.gradFrom} to-white bg-clip-text text-transparent`}>
            for {cat.short}
          </span>
        </h1>
        <p className="text-slate-400 mt-3 max-w-2xl text-[14.5px] leading-relaxed">
          Pick your traffic source below, then choose the offer. Every zip you see is scoped
          to that platform — never mix Facebook Marketplace zips with Google Ads targeting.
        </p>
        {syncedAt && (
          <p className="mt-3 text-[11.5px] text-slate-500 flex items-center gap-1.5" data-testid="target-zips-synced-at">
            <RefreshCcw className="w-3 h-3" /> Updated {fmtDate(syncedAt)}
          </p>
        )}

        {/* Traffic source tabs — the primary decision */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" role="tablist" data-testid="target-zips-category-tabs">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = c.key === category;
            const stat = categoryStats[c.key] || {};
            return (
              <button
                key={c.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setCategory(c.key)}
                data-testid={`target-zips-tab-${c.key}`}
                className={`relative overflow-hidden text-left rounded-2xl border p-5 transition-all group ${
                  active
                    ? `bg-gradient-to-br ${c.swatch} border-transparent shadow-2xl ${c.ring} ring-2`
                    : `bg-slate-900/60 border-slate-800 ${c.tile}`
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                      active ? "bg-white/20" : `${c.bg} border ${c.border}`
                    }`}>
                      <Icon className={`w-5 h-5 ${active ? "text-white" : c.accent}`} strokeWidth={2.25} />
                    </div>
                    <div>
                      <p className={`text-[10.5px] font-bold uppercase tracking-[0.16em] ${active ? "text-white/80" : "text-slate-500"}`}>
                        Traffic Source
                      </p>
                      <h3 className={`font-display text-[19px] font-bold tracking-tight ${active ? "text-white" : "text-slate-100"}`}>
                        {c.label}
                      </h3>
                    </div>
                  </div>
                  {active && (
                    <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-widest text-white/90 bg-white/15 px-2 py-1 rounded-md">
                      <CheckCircle className="w-3 h-3" /> Viewing
                    </span>
                  )}
                </div>
                <div className={`mt-4 flex items-center gap-4 text-[12px] ${active ? "text-white/90" : "text-slate-400"}`}>
                  <span><b className={`${active ? "text-white" : "text-slate-100"} text-[14px]`}>{stat.offer_count || 0}</b> offers</span>
                  <span className="opacity-40">·</span>
                  <span><b className={`${active ? "text-white" : "text-slate-100"} text-[14px]`}>{(stat.zip_count || 0).toLocaleString()}</b> zips</span>
                  {stat.synced_at && (
                    <>
                      <span className="opacity-40">·</span>
                      <span>updated {fmtDate(stat.synced_at)}</span>
                    </>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Selector + zips */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        {/* Google-only nationwide coverage banner */}
        {category === "google" && (
          <div className="mb-8 mt-2 flex flex-col items-center text-center" data-testid="target-zips-google-nationwide-block">
            <div
              data-testid="target-zips-google-nationwide-banner"
              className="relative w-full max-w-3xl overflow-hidden rounded-2xl border border-blue-500/40 shadow-[0_18px_45px_-18px_rgba(30,64,175,0.75)]"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-800 via-blue-700 to-indigo-800" />
              <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 18% 20%, rgba(255,255,255,0.35), transparent 60%), radial-gradient(circle at 82% 80%, rgba(0,0,0,0.35), transparent 60%)" }} />
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent" />
              <div className="relative px-6 sm:px-10 py-8 sm:py-9 flex flex-col items-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/25 px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/95 mb-4">
                  <Globe2 className="w-3.5 h-3.5" /> Nationwide Reach
                </div>
                <h2
                  className="font-display font-bold tracking-tight text-white text-center leading-[1.1]"
                  style={{ fontSize: "clamp(1.75rem, 3.6vw, 2.75rem)", letterSpacing: "-0.015em" }}
                >
                  Nationwide Coverage with Best Connectivity
                </h2>
              </div>
            </div>
            <p className="mt-4 text-[13.5px] sm:text-[14.5px] text-slate-400 max-w-xl">
              Available for <span className="font-semibold text-slate-200">Google Ads</span>, <span className="font-semibold text-slate-200">Meta Ads</span>, <span className="font-semibold text-slate-200">SEO</span>, and all <span className="font-semibold text-slate-200">Paid Traffic</span>.
            </p>
          </div>
        )}

        {/* Always-visible category banner so publishers never confuse the two */}
        <div
          data-testid="target-zips-active-banner"
          className={`mb-3 rounded-xl border ${cat.border} ${cat.bg} px-4 py-2.5 flex items-center gap-2.5 text-[12.5px] ${cat.accentSoft}`}
        >
          <span className={`w-2 h-2 rounded-full ${cat.dot} animate-pulse`} />
          <cat.icon className={`w-4 h-4 ${cat.accent}`} strokeWidth={2.25} />
          <span>
            Viewing <b className={cat.accent}>{cat.label}</b> zip codes. Only use these on
            your {cat.label} campaigns.
          </span>
        </div>
        <div className={`rounded-2xl border border-slate-800/70 bg-slate-900/60 backdrop-blur-xl shadow-2xl overflow-hidden ring-1 ${cat.ring}`}>
          {/* Selector row */}
          <div className="px-5 sm:px-6 py-4 border-b border-slate-800/70 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="flex-1 min-w-0">
              <label className="block text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5">
                Select Offer
              </label>
              <div className="relative">
                <select
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                  disabled={loadingList || offers.length === 0}
                  data-testid="target-zips-offer-select"
                  className="w-full appearance-none bg-slate-950/70 border border-slate-800 hover:border-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-white text-[14px] font-semibold rounded-lg pl-4 pr-10 py-2.5 outline-none transition disabled:opacity-60"
                >
                  {loadingList && <option>Loading offers…</option>}
                  {!loadingList && offers.length === 0 && <option>No offers available yet</option>}
                  {offers.map((o) => (
                    <option key={o.offer_name} value={o.offer_name}>
                      {o.offer_name} — {o.zip_count.toLocaleString()} zips
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
              </div>
            </div>
            <div className="flex-1 max-w-sm">
              <label className="block text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5">
                Filter
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Start typing a zip…"
                  data-testid="target-zips-search"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-slate-950/70 border border-slate-800 text-[13.5px] text-slate-100 placeholder-slate-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition"
                />
              </div>
            </div>
          </div>

          {/* Zips */}
          <div className="p-5 sm:p-6">
            {error && (
              <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-300" data-testid="target-zips-error">
                {error}
              </div>
            )}

            {loadingZips ? (
              <div className="p-10 flex items-center justify-center text-slate-500 text-[13px]">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading zip codes…
              </div>
            ) : offers.length === 0 ? (
              <div className="p-10 text-center">
                <ShieldAlert className={`w-8 h-8 mx-auto ${cat.accent} mb-2`} />
                <p className="text-[13.5px] text-slate-300">
                  No {cat.label} zips published yet.
                </p>
                <p className="text-[12px] text-slate-500 mt-1">
                  Try the other traffic source above, or check back after our next sync.
                </p>
              </div>
            ) : filteredZips.length === 0 ? (
              <div className="p-10 text-center">
                <p className="text-[13.5px] text-slate-400">
                  {q ? `No zips match "${q}".` : "No zip codes for this offer."}
                </p>
              </div>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div className="text-[12.5px] text-slate-400">
                    <span className="font-bold text-white text-[15px]" data-testid="target-zips-count">
                      {filteredZips.length.toLocaleString()}
                    </span>{" "}
                    {filteredZips.length === zips.length ? "zip codes" : `of ${zips.length.toLocaleString()} zips`}
                    {zipsSyncedAt && (
                      <span className="ml-2 text-slate-500">· updated {fmtDate(zipsSyncedAt)}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={copyCommaSeparated}
                      data-testid="target-zips-copy-csv"
                      className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-md border border-slate-700 text-slate-200 hover:text-white hover:border-slate-500 transition"
                    >
                      {copied === "csv" ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied === "csv" ? "Copied" : "Copy comma-separated"}
                    </button>
                    <button
                      onClick={copyAll}
                      data-testid="target-zips-copy-all"
                      className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-md bg-gradient-to-r ${cat.swatch} hover:brightness-110 text-white shadow-lg transition`}
                    >
                      {copied === "all" ? <CheckCircle className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied === "all" ? "Copied" : "Copy all"}
                    </button>
                  </div>
                </div>

                {/* Zip grid — clean, tabular */}
                <div
                  data-testid="target-zips-grid"
                  className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5 font-mono text-[12.5px]"
                >
                  {filteredZips.map((z) => (
                    <span
                      key={z}
                      className={`px-2 py-1 rounded-md bg-slate-950/60 border border-slate-800/70 text-slate-200 text-center ${cat.tile} hover:text-white transition select-all`}
                    >
                      {z}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-600 mt-6">
          © {new Date().getFullYear()} Digital Master Media LLC. Coverage areas are updated
          continuously as buyers adjust their targeting.
        </p>
      </section>
    </div>
  );
}
